import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { gymsService } from '../../services/gyms';
import { GymHoursEditor, type HoursState } from '../../components/gym/GymHoursEditor';
import { useCurrentGym } from '../hooks/useCurrentGym';

export default function HoursPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { gym, loading, setGym, slug } = useCurrentGym();

  const [hours, setHours] = useState<HoursState>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (gym) setHours(gym.hours || {});
  }, [gym]);

  const handleSave = async () => {
    if (!slug) return;
    setSaving(true);
    try {
      const updated = await gymsService.updateGym(slug, {
        hours: Object.keys(hours).length > 0 ? hours : undefined,
      });
      setGym(updated);
      toast({ title: t('gymAdmin.saved') });
    } catch (error) {
      toast({
        title: t('gymAdmin.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Skeleton className="h-96 w-full max-w-3xl" />;
  if (!gym) return null;

  return (
    <div className="max-w-3xl space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('gymAdmin.hours.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <GymHoursEditor value={hours} onChange={setHours} />
          <Button onClick={handleSave} disabled={saving}>
            {saving ? t('gymAdmin.saving') : t('gymAdmin.save')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
